import downloadFile from 'js-file-download';
import {xor} from 'lodash-es';
import {
    useEffect, useRef, useState,
} from 'react';

import {PDF_FALLBACK_TOKEN_KEY} from 'pages/pdf-link-fallback-page/pdf-link-fallback-page-constants';
import {ELinkType, ELinkMethod, TLinkParameters} from 'shared/types/links';
import {showMessage} from 'shared/utils';

import {PdfPreviewService} from './service/pdf-preview.service';

type TUseFileViewerArgs = ({
    baseUrl: string;
    url?: never;
} | {
    baseUrl?: never;
    url: string;
}) & {
    locale?: {
        downloadError?: string;
    };
    urlType?: ELinkType | null;
}

export type TLoadFileArgs = {
    id?: string;
    defaultTitle?: string;
    headers?: Array<[string, string | undefined]>;
    method?: ELinkMethod | null;
    body?: TLinkParameters | null;
    onStartLoading?: () => void;
    onCompleteLoading?: () => void;
}

export const useFileViewer = (args: TUseFileViewerArgs) => {
    const {
        baseUrl, url: strictUrl, locale = {}, urlType,
    } = args;

    const {
        downloadError = 'Произошла ошибка скачивания файла',
    } = locale ?? {};

    const [urlsBeingLoaded, setUrlsBeingLoaded] = useState<string[]>([]);
    const isUnmountedRef = useRef(false);

    useEffect(() => {
        isUnmountedRef.current = false;
        return () => {
            isUnmountedRef.current = true;
        };
    }, []);

    const getUrl = (id?: string) => {
        if (strictUrl) return strictUrl;

        return `${baseUrl}/${id}`;
    };

    const checkIsLoading = (id?: string) => (
        urlsBeingLoaded.includes(getUrl(id))
    );

    const loadFile = (loadFileArgs?: TLoadFileArgs) => {
        const {
            defaultTitle,
            id,
            headers,
            onCompleteLoading,
            onStartLoading,
            method,
            body
        } = loadFileArgs ?? {};

        const xhr = new XMLHttpRequest();
        const url = getUrl(id);
        const xhrMethod = method || "GET";

        xhr.open(xhrMethod, url, true);
        xhr.responseType = 'arraybuffer';
        (headers ?? []).forEach(headerTuple => {
            const [name, value] = headerTuple;
            if (name && value) xhr.setRequestHeader(name, value);
        });
        xhr.onerror = () => {
            onCompleteLoading?.();

            setUrlsBeingLoaded(p => {
                if (p.includes(url)) return xor(p, [url]);
                return p;
            });
        };
        xhr.onloadstart = () => {
            onStartLoading?.();
        };
        xhr.onload = async () => {
            onCompleteLoading?.();

            if (xhr.status === 200) {
                let fileName = defaultTitle;

                const contentDisposition = xhr.getResponseHeader('content-disposition');
                const contentType = xhr.getResponseHeader('Content-Type');
                const isPDF = contentType?.match('application/pdf');

                if (contentDisposition) {
                    const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    const match = fileNameRegex.exec(contentDisposition);
                    if (match) {
                        fileName = match[1].replace(/['"]/g, '');
                        try {
                            fileName = decodeURIComponent(fileName);
                        } catch (e) {
                            console.warn('Ошибка декодирования имени файла: ', e);
                            fileName = defaultTitle;
                        }
                    }
                }

                if (!isUnmountedRef.current) {
                    if (!isPDF) {
                        downloadFile(xhr.response, fileName ?? defaultTitle ?? 'ПУД');

                        setUrlsBeingLoaded(p => {
                            if (p.includes(url)) return xor(p, [url]);
                            return p;
                        });
                        return;
                    }

                    const blob = new Blob([xhr.response], {type: 'application/pdf'});

                    const isPdfPreviewEnabled = await PdfPreviewService.getPreviewPdfSetting();

                    if (isPdfPreviewEnabled) {
                      PdfPreviewService.openPreviewWindow(blob);
                    } else {
                      PdfPreviewService.downloadPdf(blob, fileName ?? 'Документ.pdf');
                    }
                }
            }
            if (xhr.status !== 200 && urlType === ELinkType.PDF_WITH_ERROR_FALLBACK) {
                setUrlsBeingLoaded(p => {
                    if (p.includes(url)) return xor(p, [url]);
                    return p;
                });

                const token = (headers?.find(([key]) => key === 'Authorization'))?.[1];

                if (!token) {
                    return;
                }

                sessionStorage.setItem(PDF_FALLBACK_TOKEN_KEY, token);
                window.open(`/pdf-link-fallback?redirectTo=${url}`, '_blank');
                return;
            }
            if (xhr.status === 404) {
                showMessage({message: downloadError, isError: true});
            }

            setUrlsBeingLoaded(p => {
                if (p.includes(url)) return xor(p, [url]);
                return p;
            });
        };

        if (body) {
            xhr.setRequestHeader("Content-Type", "application/json");
            xhr.send(JSON.stringify(body));
        } else {
            xhr.send();
        }

        setUrlsBeingLoaded(p => {
            if (!p.includes(url)) return [...p, url];
            return p;
        });
    };

    return {
        loadFile,
        checkIsLoading,
        urlsBeingLoaded,
    };
};
